import { 
  TableCell, 
  Paper, 
  Table, 
  TableBody, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Checkbox 
} from "@mui/material";
import { useContext, useMemo, useState, useCallback, memo } from 'react';
import { useTheme } from '@mui/material/styles';
import { ProjectContext } from '../../../contexts/ProjectContext';
import { ElementButtonDelete } from "../ElementProperties/elementButtonDelete";
import { ElementButtonEdit } from "../ElementProperties/elementButtonEdit";


export const ElementsView = memo((props) => {
    const { elements } = useContext(ProjectContext);
    const theme = useTheme();
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    
    // Destructure props to avoid dependency issues
    const { 
        sortBy, 
        sortDirection, 
        onSortBy, 
        canvasRef 
    } = props;

    // Memoize filtered and sorted elements to avoid unnecessary re-computations
    const filteredElements = useMemo(() => 
        elements.filter((row) => row.group === "nodes"), 
        [elements]
    );

    const allIds = useMemo(() => 
        filteredElements.map(e => e?.data?.id).filter(Boolean), 
        [filteredElements]
    );

    const sortedElements = useMemo(() => {
        if (sortBy !== 'name') return filteredElements;
        
        return [...filteredElements].sort((a, b) => {
            const nameA = (a?.data?.name || '').toLowerCase();
            const nameB = (b?.data?.name || '').toLowerCase();
            if (nameA < nameB) return sortDirection === 'desc' ? 1 : -1;
            if (nameA > nameB) return sortDirection === 'desc' ? -1 : 1;
            return 0;
        });
    }, [filteredElements, sortBy, sortDirection]);

    const numSelected = selectedIds.size;
    const rowCount = allIds.length;

    const isAllSelected = rowCount > 0 && numSelected === rowCount;
    const isIndeterminate = numSelected > 0 && numSelected < rowCount;

    const handleToggleAll = useCallback((event) => {
      const { checked } = event.target;
      setSelectedIds(checked ? new Set(allIds) : new Set());
    }, [allIds]);

    const handleToggleOne = useCallback((id) => (event) => {
      const { checked } = event.target;
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    }, []);

    const handleSort = useCallback(() => {
      if (onSortBy) {
        const newDirection = sortBy === 'name' && sortDirection === 'asc' ? 'desc' : 'asc';
        onSortBy('name', newDirection);
      }
    }, [onSortBy, sortBy, sortDirection]);

    return (
        <TableContainer component={Paper}>
      <Table sx={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: 48 }} />
          <col style={{ width: 200 }} />
          <col />
          <col style={{ width: 120 }} />
        </colgroup>
        <TableHead sx={{ backgroundColor: theme.palette.appBarBg.main, '& .MuiTableCell-root': { color: 'common.white' } }}>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox 
                size="small" 
                indeterminate={isIndeterminate} 
                checked={isAllSelected} 
                onChange={handleToggleAll} 
                sx={{ color: 'common.white', '&.Mui-checked': { color: 'common.white' } }} 
              />
            </TableCell>
            <TableCell
              sortDirection={sortDirection || false}
              sx={{ cursor: "pointer", userSelect: "none" }}
              onClick={handleSort}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Name
                {sortBy === 'name' && (
                  <span style={{ marginLeft: 4 }}>
                    {sortDirection === 'desc' ? '▼' : '▲'}
                  </span>
                )}
              </span>
            </TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">
              {numSelected > 1 ? (
                <ElementButtonDelete 
                  canvasRef={canvasRef} 
                  ids={Array.from(selectedIds)} 
                  color={theme.palette.common.white} 
                  tooltip={`Delete ${numSelected} selected`} 
                />
              ) : (
                'Actions'
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedElements.map((row) => (
            <TableRow
              key={row?.data.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell padding="checkbox">
                <Checkbox 
                  size="small" 
                  checked={selectedIds.has(row?.data?.id)} 
                  onChange={handleToggleOne(row?.data?.id)} 
                />
              </TableCell>
              <TableCell component="th" scope="row">
                {row?.data?.name}
              </TableCell>
              <TableCell>{row?.data?.description}</TableCell>
              <TableCell align="right">
                <ElementButtonEdit 
                  canvasRef={canvasRef} 
                  item={row} 
                  type={row?.group} 
                  color={theme.palette.common.black} 
                />
                <ElementButtonDelete 
                  canvasRef={canvasRef} 
                  id={row?.data?.id} 
                  color={theme.palette.common.black} 
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    );
});